import * as React from 'react'
import { ErrorBoundary } from '@rebeldotcom/ui'
import * as RebelPanels from '../components/panels'
import { GetSiteMetaData } from '../components/metadata'

export const tldReplace = (str, dictionary) => {
  let newStr = str
  const keys = Object.keys(dictionary)
  keys.forEach(k => {
    newStr = newStr.replace(`{{${k}}}`, dictionary[k])
  })
  return newStr
}

// We use kebab-case when referring to a panel in a JSON page file
// We use PascalCase for naming components
export const getPanelComponent = panelType => {
  const panelKey = Object.keys(RebelPanels).find(
    p => p.toLowerCase() === panelType.replace(/-/g, '')
  )
  return RebelPanels[panelKey]
}

const getDynamicImage = (images, tldImages, dynamicImage) => {
  const dynamicPath = dynamicImage.path.replace(`{{`, '').replace('}}', '')
  const tldImage = tldImages[dynamicPath]
  const image = images.find(i => {
    return i.originalName === tldImage.path
  })
  return { fluid: image, altText: tldImage.altText }
}

const loadHeroTemplate = ({ panel, dictionary, allImages }) => {
  panel.alignment = 'left'
  panel.title.main.text = tldReplace(panel.title.main.text, dictionary)
  panel.tlds = [dictionary.ItemName]

  if (panel.dynamicImage && allImages) {
    const dynamicImage = getDynamicImage(
      allImages,
      dictionary.Images,
      panel.dynamicImage
    )
    panel.image = {
      ...panel.dynamicImage,
      path: {
        childImageSharp: {
          fluid: dynamicImage.fluid,
        },
      },
      altText: dynamicImage.altText,
    }
  }
  return panel
}

const loadSpreadTemplate = ({ panel, dictionary }) => {
  panel.alignment = 'left'
  panel.title.main.text = tldReplace(panel.title.main.text, dictionary)
  panel.tlds = [dictionary.ItemName]
  if (panel.hasDynamicIcons && !(dictionary.Items === undefined)) {
    dictionary.Items.forEach((item, i) => {
      panel.items[i].icon = item.icon
      panel.items[i].iconColor = item.iconColor
      panel.items[i].title.main = item.title
    })
  }
  return panel
}

const loadCTASplitTemplate = ({
  panel,
  dictionary,
  allImages,
  supportLinkTitle,
}) => {
  const siteMetaData = GetSiteMetaData()
  panel.title.main.text = tldReplace(panel.title.main.text, dictionary)
  panel.content = tldReplace(panel.content, dictionary)

  if (dictionary.Support) {
    if (!Array.isArray(dictionary.Support)) {
      dictionary.Support = [dictionary.Support]
    }
    const supportLinks = dictionary.Support.map(t => {
      if (!t[siteMetaData.shortName]) {
        return ''
      }
      return `<p><a href="${t[siteMetaData.shortName]}">${
        t.Title ? t.Title : tldReplace(supportLinkTitle, dictionary)
      }</a></p>`
    })
    panel.content = `${panel.content}${supportLinks.join('\n')}`
  }

  if (panel.product?.dynamicImage) {
    const dynamicImage = getDynamicImage(
      allImages,
      dictionary.Images,
      panel.product.dynamicImage
    )
    panel.product.image = {
      ...panel.product.dynamicImage,
      path: {
        childImageSharp: {
          fluid: dynamicImage.fluid,
        },
      },
      altText: dynamicImage.altText,
    }
  }
  return panel
}

export const getTemplatePanels = ({
  panels,
  dictionary,
  allImages,
  supportLinkTitle,
  slug,
  variant,
}) => {
  if (!panels) return null

  return panels.map((panel, idx) => {
    const Component = getPanelComponent(panel.type)

    switch (panel.type) {
      case 'hero':
        panel = loadHeroTemplate({ panel, dictionary, allImages })
        break
      case 'spread':
        panel = loadSpreadTemplate({ panel, dictionary })
        break
      case 'cta-split':
        panel = loadCTASplitTemplate({
          panel,
          dictionary,
          allImages,
          supportLinkTitle,
        })
        break
      default:
        break
    }

    if (!Component) {
      // eslint-disable-next-line no-console
      console.warn(
        `Couldn't find a component for ${panel.type}, please implement me`
      )
      return null
    }

    return (
      <ErrorBoundary key={idx} location={`${slug}-${panel.type}`}>
        <Component key={idx} data={panel} variant={variant} />
      </ErrorBoundary>
    )
  })
}
